// The Project Leviathan secret feature on /member-unavailable, shared by the
// page and by the HyperFrames composition in this folder (index.html), so the
// rendered video and the page play the same drawing, dialogue and timing.
//
// An original argument in the spirit of Silicon Valley's Erlich and Jian-Yang,
// with nods to their smart fridge scene and the fish in the sink. Trade With
// Titans has locked members out for a secret feature, and Jian-Yang has
// something under a cloth called Project Leviathan. He describes BowlBot like
// an appliance: load it up, push one button, it does everything, no human.
// Erlich hears "BullBot", an AI that trades the bull market for them. Jian-Yang
// lets him believe it, takes his palapa and makes him clean the sink until it
// is finished, fish and all. It is a smart dishwasher, built to make Erlich
// feel bad, it is not finished, and Erlich ends up washing the bowls. The
// feature in the joke is made up.
//
// The first "Jian-Yang," is Erlich's real voice from the reference video. The
// rest is one eleven_v4 Text-to-Dialogue take (jobs/bowlbot-v4-dishes/mix/c).
// We see both of them across a table. Their lines appear as speech bubbles,
// so the joke reads with the sound off.
//
// One scene clock drives everything. With sound on it is audio.currentTime.
// With sound off the same clock runs through a stretched timeline that waits
// on each bubble long enough to read it (sceneTimeAtSilent). Blinks and
// breathing follow a second, wall clock argument so the picture stays alive
// while it waits. renderScene(scene, t, wall) is a pure function of its
// arguments, so seeking and rendering are deterministic.

/** Length of the sound film in seconds. */
export const SCENE_DURATION = 62.93;

/** The drawing's coordinate space. Desktop shows y 0 to 600, phones a taller crop. */
export const SCENE_WIDTH = 960;
export const SCENE_HEIGHT = 600;
const VIEW_TOP = -300;

/** Speaker names for the transcript. The picture itself never labels them. */
export const SPEAKERS = { erlich: "Erlich", jian_yang: "Jian-Yang" };

/**
 * The lines, in order, for the transcript.
 * @type {Array<{ speaker: "erlich" | "jian_yang", text: string }>}
 */
export const DIALOGUE = [
  {
    speaker: "erlich",
    text: "Jian-Yang, just tell me what Project Leviathan is. Trade With Titans locked out every member for it. That smells like money.",
  },
  { speaker: "jian_yang", text: "Is BowlBot. Very smart machine. You load it up, push one button, and it do everything. No human." },
  {
    speaker: "erlich",
    text: "A BullBot. Load it up with cash, push one button, and it trades the bull market for us. No humans. That's genius.",
  },
  { speaker: "jian_yang", text: "Yes. Very expensive." },
  { speaker: "erlich", text: "Count me in. Ten percent. Name your price." },
  {
    speaker: "jian_yang",
    text: "Your palapa. And until it is finished, you clean the sink. Even though you no eat the fish.",
  },
  { speaker: "erlich", text: "Done. Done! Show me the bot." },
  {
    speaker: "jian_yang",
    text: "This is BowlBot. Not just any BowlBot. A smart BowlBot. Fourteen thousand dollar. I build it for me, to make you feel bad.",
  },
  { speaker: "erlich", text: "I gave you my half-a-million-dollar palapa for ten percent of a dishwasher?" },
  { speaker: "jian_yang", text: "Dishwasher not finished. So tonight, you are BowlBot." },
];

/**
 * Speech bubbles: who, when the first word is spoken, when the last word ends,
 * and the words. The opening "Jian-Yang," is heard but not written.
 * @type {Array<["erlich" | "jian_yang", number, number, string]>}
 */
const PAGES = [
  ["erlich", 1.35, 3.7, "Just tell me what Project Leviathan is."],
  ["erlich", 4.35, 8.14, "Trade With Titans locked out every member for it. That smells like money."],
  ["jian_yang", 8.55, 11.74, "Is BowlBot. Very smart machine."],
  ["jian_yang", 12.37, 17.55, "You load it up, push one button, and it do everything. No human."],
  ["erlich", 17.88, 18.59, "A BullBot."],
  ["erlich", 19.25, 24.08, "Load it up with cash, push one button, and it trades the bull market for us."],
  ["erlich", 24.17, 26.19, "No humans. That's genius."],
  ["jian_yang", 26.69, 28.6, "Yes. Very expensive."],
  ["erlich", 29.39, 31.83, "Count me in. Ten percent. Name your price."],
  ["jian_yang", 32.03, 32.78, "Your palapa."],
  ["jian_yang", 33.37, 38.53, "And until it is finished, you clean the sink. Even though you no eat the fish."],
  ["erlich", 38.9, 40.97, "Done. Done! Show me the bot."],
  ["jian_yang", 41.1, 42.49, "This is BowlBot."],
  ["jian_yang", 43.17, 46.12, "Not just any BowlBot. A smart BowlBot."],
  ["jian_yang", 46.58, 51.37, "Fourteen thousand dollar. I build it for me, to make you feel bad."],
  ["erlich", 51.67, 56.24, "I gave you my half-a-million-dollar palapa for ten percent of a dishwasher?"],
  ["jian_yang", 56.8, 58.31, "Dishwasher not finished."],
  ["jian_yang", 58.8, 61.07, "So tonight, you are BowlBot."],
];

/**
 * When each of them is talking, for the mouths.
 * @type {Array<["erlich" | "jian_yang", number, number]>}
 */
const SPEECH = [
  ["erlich", 0.12, 0.8],
  ["erlich", 1.35, 3.67],
  ["erlich", 4.35, 8.11],
  ["jian_yang", 8.55, 9.33],
  ["jian_yang", 10.17, 11.71],
  ["jian_yang", 12.37, 15.97],
  ["jian_yang", 16.7, 17.52],
  ["erlich", 17.88, 18.56],
  ["erlich", 19.25, 24.91],
  ["erlich", 25.36, 26.16],
  ["jian_yang", 26.69, 27.11],
  ["jian_yang", 27.59, 28.57],
  ["erlich", 29.39, 31.8],
  ["jian_yang", 32.03, 32.75],
  ["jian_yang", 33.37, 34.73],
  ["jian_yang", 35.17, 38.5],
  ["erlich", 38.9, 40.94],
  ["jian_yang", 41.1, 42.46],
  ["jian_yang", 43.17, 44.51],
  ["jian_yang", 44.95, 46.09],
  ["jian_yang", 46.58, 47.68],
  ["jian_yang", 48.66, 51.34],
  ["erlich", 51.67, 52.95],
  ["erlich", 53.63, 56.21],
  ["jian_yang", 56.8, 58.28],
  ["jian_yang", 58.8, 59.5],
  ["jian_yang", 59.84, 61.04],
];

/** When the words that move the drawing are spoken, in seconds. */
const W = {
  project: 2.25,
  trade: 4.35,
  every: 5.37,
  smells: 7.15,
  isBowlBot: 8.91,
  smartMachine: 10.83,
  load: 12.57,
  pushButton: 13.57,
  button: 14.23,
  noHuman: 16.7,
  aBullBot: 17.88,
  bullBot: 18.14,
  loadItUp: 19.25,
  pushButtonAgain: 20.85,
  trades: 22.27,
  noHumans: 24.17,
  thatsGenius: 25.36,
  genius: 25.74,
  yes: 26.69,
  veryExpensive: 27.59,
  countMeIn: 29.39,
  tenPercent: 30.12,
  nameYourPrice: 30.86,
  yourPalapa: 32.03,
  andUntil: 33.37,
  cleanTheSink: 35.35,
  sink: 35.81,
  evenThough: 36.46,
  fish: 38.08,
  done: 38.9,
  doneAgain: 39.52,
  showMe: 40.08,
  thisIs: 41.1,
  revealBowlBot: 42.04,
  notJustAny: 43.17,
  smartBowlBot: 45.17,
  fourteen: 46.6,
  buildItForMe: 48.66,
  feelBad: 50.64,
  iGave: 51.67,
  halfAMillion: 52.53,
  palapa: 53.63,
  tenPercentAgain: 54.59,
  dishwasher: 55.79,
  notFinished: 56.8,
  not: 57.48,
  finished: 57.86,
  soTonight: 58.8,
  youAre: 59.84,
  youAreBowlBot: 60.62,
};

// ---------------------------------------------------------------------------
// The muted timeline. Each bubble stays up long enough to read: a second, plus
// a third of a second a word, and the drawing waits just before the next
// bubble's first word. A few beats get extra time to land.

/** @type {Record<number, number>} extra seconds after a bubble, by bubble index */
const LANDING = { 4: 0.5, 6: 0.4, 10: 0.5, 12: 0.8, 13: 0.4, 14: 0.6, 15: 0.4, 17: 1.8 };

const SILENT_SEGMENTS = (() => {
  /** @type {Array<{ at: number, hold: number }>} */
  const holds = [];
  PAGES.forEach((page, i) => {
    const next = PAGES[i + 1];
    const at = next ? next[1] - 0.05 : SCENE_DURATION;
    const words = page[3].split(/\s+/).length;
    const reading = 1 + 0.34 * words;
    holds.push({ at, hold: Math.max(0, reading - (at - page[1])) + (LANDING[i] ?? 0) });
  });
  return holds;
})();

/** Length of the muted run in seconds. */
export const SILENT_DURATION = SCENE_DURATION + SILENT_SEGMENTS.reduce((sum, h) => sum + h.hold, 0);

/**
 * The scene time shown s seconds into the muted run.
 * @param {number} s
 */
export function sceneTimeAtSilent(s) {
  let waited = 0;
  for (const { at, hold } of SILENT_SEGMENTS) {
    if (s < at + waited) return s - waited;
    if (s < at + waited + hold) return at;
    waited += hold;
  }
  return Math.min(SCENE_DURATION, s - waited);
}

/**
 * Where the muted run is when the scene shows time t.
 * @param {number} t
 */
export function silentTimeAtScene(t) {
  let waited = 0;
  for (const { at, hold } of SILENT_SEGMENTS) {
    if (t <= at) return t + waited;
    waited += hold;
  }
  return Math.min(SILENT_DURATION, t + waited);
}

// ---------------------------------------------------------------------------
// Colors

const IVORY = "#f6f3ea";
const PAPER = "#fffdf8";
const INK = "#14120f";
const INK_SOFT = "#2f2d29";
const MUTED = "#6a5d4c";
const TERRACOTTA = "#d97757";
const RUST = "#9a4d2c";
const LINE = "#d7cfbf";
const TABLE_TOP = "#ebe2d1";
const TABLE_FRONT = "#ddd1bb";
const STRAW = "#d9b873";
const STRAW_DARK = "#b08a4a";
const WOOD = "#8a6a48";
// Erlich: curly brown hair and beard, brown quarter-zip over a blue shirt.
const E_HAIR = "#6b4a2f";
const E_HAIR_DARK = "#4b331f";
const E_SKIN = "#efcdb0";
const E_SWEATER = "#8b6b4b";
const E_SWEATER_DARK = "#735739";
const E_SHIRT = "#7f9fba";
// Jian-Yang: black bowl cut, black rectangular glasses, red tracksuit with white stripes.
const J_HAIR = "#1d1a17";
const J_SKIN = "#f0d2b3";
const J_JACKET = "#c4473a";
const J_JACKET_DARK = "#a63b30";
const MOUTH = "#3d2019";

// The display on Project Leviathan, then on BowlBot. Only the letters that
// differ flip, so BULLBOT and BOWLBOT trade two letters.
const FLAP_SLOTS = 7;
/** @type {Array<[number, string]>} */
const FLAP_WORDS = [
  [0, "SECRET "],
  [W.isBowlBot, "BOWLBOT"],
  [W.bullBot, "BULLBOT"],
  [W.revealBowlBot, "BOWLBOT"],
];

// ---------------------------------------------------------------------------
// Easing and keyframe tracks

/** @typedef {(p: number) => number} Ease */

/** @param {number} p */
const easeIn = (p) => p * p;
/** @param {number} p */
const out = (p) => 1 - (1 - p) * (1 - p);
/** @param {number} p */
const out3 = (p) => 1 - Math.pow(1 - p, 3);
/** @param {number} p */
const inOut = (p) => (p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2);
/** @param {number} p */
const expoOut = (p) => (p >= 1 ? 1 : 1 - Math.pow(2, -10 * p));
/** @param {number} p */
const linear = (p) => p;

/**
 * A keyframe track. Each key is [time, value, ease]. The ease shapes the
 * segment that ends at that key, and defaults to inOut.
 * @param {Array<[number, number, Ease?]>} keys
 * @returns {(t: number) => number}
 */
function track(keys) {
  return (t) => {
    let previous = keys[0];
    if (!previous) return 0;
    if (t <= previous[0]) return previous[1];
    for (const key of keys) {
      if (t <= key[0]) {
        const span = key[0] - previous[0];
        const p = span <= 0 ? 1 : (t - previous[0]) / span;
        const shape = key[2] || inOut;
        return previous[1] + (key[1] - previous[1]) * shape(p);
      }
      previous = key;
    }
    return previous[1];
  };
}

/**
 * Two tracks from keys of [time, x, y, ease].
 * @param {Array<[number, number, number, Ease?]>} keys
 */
function track2(keys) {
  return {
    x: track(keys.map(([t, x, , e]) => [t, x, e])),
    y: track(keys.map(([t, , y, e]) => [t, y, e])),
  };
}

/** @param {number} value */
const clamp01 = (value) => Math.min(1, Math.max(0, value));

/**
 * A step track of named states: the last one whose time has passed.
 * @template {string} T
 * @param {Array<[number, T]>} keys
 * @returns {(t: number) => T}
 */
function steps(keys) {
  return (t) => {
    let current = /** @type {[number, T]} */ (keys[0])[1];
    for (const [at, value] of keys) {
      if (t >= at) current = value;
    }
    return current;
  };
}

/**
 * A short bump when a word names something.
 * @param {number} t
 * @param {number} at
 */
function bump(t, at) {
  const d = t - at;
  return d < 0 || d > 0.4 ? 0 : Math.sin((Math.PI * d) / 0.4);
}

/** @param {number} n */
const f = (n) => n.toFixed(2);

/**
 * @param {string} attrs
 * @param {string} body
 */
const text = (attrs, body) =>
  `<text ${attrs} style="font-family: var(--mu-sans, system-ui, sans-serif)">${body}</text>`;

/**
 * A row of bumps along an ellipse, for curls, a beard edge or a cloud. Angles
 * in degrees, measured clockwise from the right.
 * @param {number} cx
 * @param {number} cy
 * @param {number} rx
 * @param {number} ry
 * @param {number} from
 * @param {number} to
 * @param {number} count
 * @param {number} bulge 0 to 1
 */
function scallops(cx, cy, rx, ry, from, to, count, bulge) {
  const point = (/** @type {number} */ deg) => {
    const a = (deg * Math.PI) / 180;
    return { x: cx + rx * Math.cos(a), y: cy + ry * Math.sin(a) };
  };
  const first = point(from);
  let d = `M${f(first.x)} ${f(first.y)}`;
  for (let i = 1; i <= count; i++) {
    const end = point(from + ((to - from) * i) / count);
    const begin = point(from + ((to - from) * (i - 1)) / count);
    const radius = Math.hypot(end.x - begin.x, end.y - begin.y) * (0.62 - 0.12 * bulge);
    d += `A${f(radius)} ${f(radius)} 0 0 1 ${f(end.x)} ${f(end.y)}`;
  }
  return d;
}

// ---------------------------------------------------------------------------
// Faces. Each mouth, brow and eye state is a small shape set by name.

/** @type {Record<string, string>} */
const ERLICH_MOUTHS = {
  closed: "M229 319Q241 323 253 318",
  smile: "M226 315Q241 327 257 314",
  grin: "M225 313Q241 315 258 312Q255 330 241 331Q228 330 225 313Z",
  talk: "M229 315Q241 312 253 315Q251 328 241 330Q231 328 229 315Z",
  wide: "M227 313Q241 309 255 313Q253 333 241 335Q229 333 227 313Z",
  o: "M235 315Q241 310 247 315Q248 327 241 329Q234 327 235 315Z",
  frown: "M229 323Q241 314 253 323",
};
/** @type {Record<string, string>} */
const JY_MOUTHS = {
  flat: "M723 325L743 324",
  smirk: "M722 326Q733 328 745 320",
  talk: "M726 323Q733 321 740 323Q739 328 733 329Q727 328 726 323Z",
  open: "M725 322Q733 320 741 322Q740 331 733 332Q726 331 725 322Z",
  proud: "M722 323Q733 330 745 322",
};

/**
 * Erlich's expression at a time: brow lift (px), brow tilt (deg, positive
 * worried), upper lid (0 open, 1 closed), gaze offset, and his resting mouth.
 * @typedef {{ brow: number, tilt: number, lid: number, gx: number, gy: number, mouth: string }} Face
 */
/** @type {Face} */
const E_LISTEN = { brow: -1, tilt: 0, lid: 0.42, gx: 4, gy: 0, mouth: "closed" };
/** @type {Face} */
const J_FLAT = { brow: 0, tilt: 0, lid: 0.38, gx: -2, gy: 0, mouth: "flat" };
/** @type {Record<string, Face>} */
const ERLICH_FACES = {
  coax: { brow: -4, tilt: 4, lid: 0.32, gx: 3, gy: 0, mouth: "smile" },
  greedy: { brow: -7, tilt: -3, lid: 0.18, gx: 3, gy: 0, mouth: "grin" },
  listen: E_LISTEN,
  hooked: { brow: -6, tilt: 2, lid: 0.12, gx: 4, gy: -1, mouth: "smile" },
  awed: { brow: -9, tilt: 4, lid: 0.05, gx: 2, gy: -4, mouth: "wide" },
  eager: { brow: -6, tilt: -2, lid: 0.2, gx: 4, gy: 0, mouth: "grin" },
  confused: { brow: -2, tilt: 8, lid: 0.3, gx: 4, gy: 1, mouth: "closed" },
  stunned: { brow: -10, tilt: 6, lid: 0, gx: 4, gy: 0, mouth: "o" },
  defeated: { brow: 2, tilt: 9, lid: 0.62, gx: -1, gy: 3, mouth: "frown" },
};
/** @type {Record<string, Face>} */
const JY_FACES = {
  flat: J_FLAT,
  notice: { brow: -1, tilt: -3, lid: 0.5, gx: -3, gy: 0, mouth: "flat" },
  smirk: { brow: 0, tilt: -4, lid: 0.46, gx: -3, gy: 0, mouth: "smirk" },
  proud: { brow: -2, tilt: 0, lid: 0.55, gx: -1, gy: -1, mouth: "proud" },
};

const erlichFace = steps([
  [0, "coax"],
  [W.smells, "greedy"],
  [8.3, "listen"],
  [W.load, "hooked"],
  [W.aBullBot, "awed"],
  [W.genius - 0.4, "greedy"],
  [W.yes - 0.2, "listen"],
  [W.countMeIn, "eager"],
  [W.yourPalapa, "listen"],
  [W.evenThough + 0.4, "confused"],
  [W.done, "eager"],
  [W.revealBowlBot, "confused"],
  [W.smartBowlBot + 0.2, "stunned"],
  [W.youAreBowlBot, "defeated"],
]);
const jyFace = steps([
  [0, "flat"],
  [W.loadItUp + 0.3, "notice"],
  [W.genius, "smirk"],
  [W.countMeIn, "flat"],
  [W.done, "smirk"],
  [W.thisIs, "proud"],
  [W.iGave, "flat"],
  [W.youAreBowlBot + 0.2, "smirk"],
]);

/**
 * A mouth while talking: open on syllables, shut between phrases.
 * @param {"erlich" | "jian_yang"} who
 * @param {number} t
 */
function talking(who, t) {
  for (const [speaker, a, b] of SPEECH) {
    if (speaker !== who || t < a || t > b) continue;
    const beat = Math.sin(t * 2 * Math.PI * (who === "erlich" ? 4.4 : 3.6) + a * 7);
    return beat > -0.2 ? (beat > 0.55 ? 2 : 1) : 0;
  }
  return -1;
}

/**
 * Blinks every few seconds of wall time.
 * @param {number} wall
 * @param {number} seed
 */
function blink(wall, seed) {
  const period = 3.7 + seed * 0.9;
  const phase = (wall + seed * 1.7) % period;
  return phase < 0.14 ? Math.sin((Math.PI * phase) / 0.14) : 0;
}

// ---------------------------------------------------------------------------
// Movement keyed to the words.

// The camera: a slow push toward whoever the beat is about.
/** @type {Array<[number, number, number, number, Ease?]>} */
const SHOTS = [
  [0, 480, 330, 1.1],
  [W.aBullBot - 0.3, 480, 330, 1.1],
  [W.aBullBot + 0.8, 420, 285, 1.166, out3],
  [W.genius + 0.6, 420, 285, 1.166],
  [W.yes + 0.4, 480, 330, 1.1, inOut],
  [W.thisIs, 480, 330, 1.1],
  [W.revealBowlBot, 560, 330, 1.21, out3],
  [W.feelBad, 560, 330, 1.21],
  [W.iGave + 0.4, 330, 290, 1.188, inOut],
  [W.notFinished - 0.3, 330, 290, 1.188],
  [W.notFinished + 0.5, 480, 330, 1.1, inOut],
];
const cameraX = track(SHOTS.map(([t, x, , , e]) => [t, x, e]));
const cameraY = track(SHOTS.map(([t, , y, , e]) => [t, y, e]));
const cameraZoom = track(SHOTS.map(([t, , , z, e]) => [t, z, e]));

// Erlich's near hand. He points at the cloth, holds up his phone, spreads his
// hand through the fantasy, slides the term sheet, stamps it, and scrubs.
const erlichHand = track2([
  [0, 350, 478],
  [W.project - 0.4, 350, 478],
  [W.project + 0.1, 470, 420, out3],
  [W.trade - 0.4, 470, 420],
  [W.trade + 0.2, 322, 318, out3],
  [W.smells - 0.3, 322, 318],
  [W.smells + 0.5, 350, 478, inOut],
  [W.loadItUp - 0.2, 350, 478],
  [W.loadItUp + 0.4, 380, 352, out3],
  [W.trades, 404, 334, inOut],
  [W.noHumans, 392, 344, inOut],
  [W.thatsGenius, 408, 326, inOut],
  [W.genius + 0.3, 350, 478, inOut],
  [W.countMeIn - 0.1, 350, 478],
  [W.countMeIn + 0.5, 494, 502, out3],
  [W.tenPercent, 498, 498],
  [W.nameYourPrice, 470, 430, inOut],
  [W.nameYourPrice + 0.8, 350, 478, inOut],
  [W.done - 0.2, 350, 478],
  [W.doneAgain - 0.12, 604, 452, inOut],
  [W.doneAgain, 604, 494, easeIn],
  [W.doneAgain + 0.4, 350, 478, inOut],
  [W.iGave + 0.2, 350, 478],
  [W.halfAMillion, 430, 424, out3],
  [W.dishwasher + 0.5, 350, 478, inOut],
  [W.youAreBowlBot + 0.2, 350, 478],
  [W.youAreBowlBot + 0.7, 330, 490, out3],
]);
// Erlich's hand shape: resting, holding the phone, open palm, holding the stamp.
const erlichGrip = steps([
  [0, "rest"],
  [W.project - 0.2, "point"],
  [W.trade - 0.2, "phone"],
  [W.smells + 0.4, "rest"],
  [W.loadItUp, "open"],
  [W.genius + 0.2, "rest"],
  [W.nameYourPrice - 0.1, "open"],
  [W.nameYourPrice + 0.6, "rest"],
  [W.done - 0.1, "stamp"],
  [W.doneAgain + 0.3, "rest"],
  [W.halfAMillion - 0.2, "open"],
  [W.dishwasher + 0.4, "rest"],
]);
// Erlich scrubs the bowl from the moment the sticker lands.
const scrubbing = track([
  [W.youAreBowlBot + 0.7, 0],
  [W.youAreBowlBot + 1.0, 1, out],
]);

// Jian-Yang's near hand. He pats the cloth, hangs a price tag on it, drags the
// palapa across, slides a sponge over, pulls the cloth off, points at himself,
// and sends the sticker across.
const jyHand = track2([
  [0, 724, 486],
  [W.smartMachine - 0.3, 724, 486],
  [W.smartMachine, 576, 330, out3],
  [W.smartMachine + 0.25, 576, 342, out],
  [W.smartMachine + 0.5, 576, 330, out],
  [W.pushButton - 0.1, 592, 316, inOut],
  [W.button, 592, 334, easeIn],
  [W.button + 0.3, 592, 316, out],
  [W.noHuman, 724, 486, inOut],
  [W.veryExpensive - 0.3, 724, 486],
  [W.veryExpensive + 0.3, 620, 404, out3],
  [W.veryExpensive + 0.9, 724, 486, inOut],
  [W.yourPalapa - 0.2, 724, 486],
  [W.yourPalapa + 0.4, 456, 462, out3],
  [W.yourPalapa + 1.4, 812, 468, inOut],
  [W.andUntil + 0.3, 724, 486, inOut],
  [W.cleanTheSink - 0.4, 724, 486],
  [W.cleanTheSink, 600, 500, out],
  [W.sink, 416, 510, inOut],
  [W.evenThough - 0.05, 614, 506, inOut],
  [W.evenThough + 0.3, 614, 508],
  [W.fish - 0.15, 484, 512, inOut],
  [W.fish + 0.45, 724, 486, inOut],
  [W.thisIs - 0.1, 724, 486],
  [W.thisIs + 0.35, 596, 322, out3],
  [W.revealBowlBot - 0.1, 596, 322],
  [W.revealBowlBot + 0.35, 790, 300, expoOut],
  [W.revealBowlBot + 1.0, 724, 486, inOut],
  [W.buildItForMe - 0.2, 724, 486],
  [W.buildItForMe + 0.3, 700, 420, out3],
  [W.feelBad + 0.5, 724, 486, inOut],
  [W.soTonight - 0.2, 724, 486],
  [W.soTonight + 0.4, 520, 450, out3],
  [W.youAreBowlBot - 0.15, 560, 420, inOut],
  [W.youAreBowlBot + 0.5, 724, 486, inOut],
]);
const jyGrip = steps([
  [0, "rest"],
  [W.pushButton - 0.2, "point"],
  [W.button + 0.4, "rest"],
  [W.veryExpensive - 0.2, "hold"],
  [W.veryExpensive + 0.4, "rest"],
  [W.yourPalapa + 0.3, "hold"],
  [W.andUntil, "rest"],
  [W.evenThough, "hold"],
  [W.fish + 0.2, "rest"],
  [W.thisIs + 0.2, "hold"],
  [W.revealBowlBot + 0.5, "rest"],
  [W.buildItForMe, "point"],
  [W.feelBad + 0.4, "rest"],
  [W.soTonight + 0.2, "point"],
  [W.youAreBowlBot + 0.3, "rest"],
]);

// The cloth over Project Leviathan comes off on "BowlBot".
const clothOff = track([
  [W.revealBowlBot - 0.05, 0],
  [W.revealBowlBot + 0.45, 1, expoOut],
]);
// The price tag goes on at "Very expensive" and becomes $14,000.
const tagOn = track([
  [W.veryExpensive, 0],
  [W.veryExpensive + 0.35, 1, out3],
]);
// Erlich's phone with the Trade With Titans lock notice.
const phoneUp = track([
  [W.trade - 0.15, 0],
  [W.trade + 0.15, 1, out],
  [W.smells + 0.1, 1],
  [W.smells + 0.4, 0, easeIn],
]);
// The term sheet slides out on "Count me in", the chip on "Ten percent", the
// stamp on the second "Done!".
const termsIn = track([
  [W.countMeIn, 0],
  [W.countMeIn + 0.5, 1, out3],
]);
const chipIn = track([
  [W.tenPercent, 0],
  [W.tenPercent + 0.2, 1, expoOut],
]);
const stampAt = W.doneAgain - 0.03;
// The palapa sits in front of Erlich until Jian-Yang drags it across.
const palapaX = track([
  [W.yourPalapa + 0.4, 432],
  [W.yourPalapa + 1.4, 792, inOut],
]);
// The sponge Jian-Yang slides to Erlich on "you clean the sink", then a plate
// of fish bones on "you no eat the fish".
const spongeX = track([
  [W.cleanTheSink, 590],
  [W.sink, 394, inOut],
]);
const fishX = track([
  [W.evenThough + 0.3, 592],
  [W.fish - 0.15, 462, inOut],
]);
// BowlBot's screen sends Erlich a message on "A smart BowlBot".
const notify = track([
  [W.smartBowlBot - 0.1, 0],
  [W.smartBowlBot + 0.2, 1, expoOut],
  [W.buildItForMe + 0.6, 1],
  [W.buildItForMe + 0.9, 0, easeIn],
]);
// Erlich's fantasy, in a thought cloud: the chart draws on "trades", the moon
// on "while we sleep", money on "cleaned up", a climb on "genius". On the
// reveal the line bends into a bowl and the bowl falls in front of him.
const cloudIn = track([
  [W.aBullBot, 0],
  [W.aBullBot + 0.5, 1, out3],
  [W.revealBowlBot + 0.9, 1],
  [W.revealBowlBot + 1.3, 0, easeIn],
]);
const lineDraw = track([
  [W.trades - 0.4, 0],
  [W.noHumans, 1, out],
]);
const buttonIn = track([
  [W.pushButtonAgain - 0.25, 0],
  [W.pushButtonAgain + 0.1, 1, out3],
]);
const noHumansIn = track([
  [W.noHumans - 0.1, 0],
  [W.noHumans + 0.25, 1, out3],
]);
const moneyIn = track([
  [W.thatsGenius - 0.2, 0],
  [W.thatsGenius + 0.3, 1, out3],
]);
const climb = track([
  [W.genius - 0.3, 0],
  [W.genius + 0.4, 1, out3],
  [W.revealBowlBot, 1],
  [W.revealBowlBot + 0.15, 0.8, inOut],
]);
const morphAt = W.revealBowlBot + 0.1;
const morph = track([
  [morphAt, 0],
  [morphAt + 0.7, 1, inOut],
]);
const bowlFall = track([
  [W.revealBowlBot + 1.0, 0],
  [W.revealBowlBot + 1.45, 1, easeIn],
]);
// BowlBot's arm waves while he shows it off, then falls off on "not".
const armWave = track([
  [W.smartBowlBot, 0],
  [W.smartBowlBot + 0.3, 1, out],
  [W.not - 0.1, 1],
]);
const armDrop = track([
  [W.not, 0],
  [W.not + 0.45, 1, easeIn],
]);
const lightOut = track([
  [W.finished, 0],
  [W.finished + 0.2, 1, linear],
]);
// The BOWLBOT sticker flies from Jian-Yang to Erlich's sweater.
const sticker = track([
  [W.youAreBowlBot - 0.15, 0],
  [W.youAreBowlBot + 0.15, 1, out],
]);
// Leans: Erlich leans in when he smells money and when he wants in.
const erlichLean = track([
  [0, 3],
  [W.smells - 0.2, 3],
  [W.smells + 0.3, 8, out],
  [W.yes, 8],
  [W.yes + 0.5, 2],
  [W.countMeIn, 2],
  [W.countMeIn + 0.4, 9, out],
  [W.doneAgain + 0.5, 9],
  [W.doneAgain + 1.0, 3],
  [W.smartBowlBot, 3],
  [W.smartBowlBot + 0.4, -6, out3],
  [W.youAreBowlBot, -6],
  [W.youAreBowlBot + 0.6, -2],
]);
const jyLean = track([
  [0, 0],
  [W.thisIs, 0],
  [W.thisIs + 0.6, -4, out],
  [W.feelBad + 0.6, -4],
  [W.iGave, 0],
]);
// Erlich slumps once he is BowlBot.
const slump = track([
  [W.youAreBowlBot, 0],
  [W.youAreBowlBot + 0.6, 1, out],
]);

// ---------------------------------------------------------------------------
// Erlich's chart line and the bowl it turns into, as matching points inside
// the thought cloud (cloud centre is 0, 0).

const LINE_POINTS = 21;
/** @param {number} i @param {number} salt */
const hash = (i, salt) => {
  const x = Math.sin(i * 127.1 + salt * 311.7) * 43758.5453;
  return x - Math.floor(x);
};
const CHART = Array.from({ length: LINE_POINTS }, (_, i) => {
  const s = i / (LINE_POINTS - 1);
  const wobble = i === 0 || i === LINE_POINTS - 1 ? 0 : (hash(i, 7) - 0.5) * 12 + (i % 2 ? -3 : 3);
  return { x: -70 + 140 * s, y: 30 - 52 * s + wobble, s };
});
const BOWL = Array.from({ length: LINE_POINTS }, (_, i) => {
  const angle = Math.PI * (1 - i / (LINE_POINTS - 1));
  return { x: 52 * Math.cos(angle), y: -6 + 34 * Math.sin(angle) };
});

/** @param {number} t */
function linePoints(t) {
  const up = climb(t);
  const m = morph(t);
  return CHART.map((point, i) => {
    const bowl = BOWL[i] ?? point;
    const cy = point.y - up * 26 * point.s * point.s;
    return { x: point.x + (bowl.x - point.x) * m, y: cy + (bowl.y - cy) * m };
  });
}

/** @param {Array<{ x: number, y: number }>} points */
const pathOf = (points) => points.map((p, i) => `${i ? "L" : "M"}${f(p.x)} ${f(p.y)}`).join("");

/**
 * A sleeve from a shoulder to a hand with a soft elbow.
 * @param {[number, number]} s shoulder
 * @param {number} hx
 * @param {number} hy
 * @param {number} bend
 */
function sleeve(s, hx, hy, bend) {
  const mx = (s[0] + hx) / 2;
  const my = (s[1] + hy) / 2;
  const dx = hx - s[0];
  const dy = hy - s[1];
  const len = Math.hypot(dx, dy) || 1;
  const ex = mx + (-dy / len) * bend;
  const ey = my + (dx / len) * bend;
  const angle = (Math.atan2(hy - ey, hx - ex) * 180) / Math.PI;
  return { d: `M${f(s[0])} ${f(s[1])}Q${f(ex)} ${f(ey)} ${f(hx)} ${f(hy)}`, angle };
}

// ---------------------------------------------------------------------------
// Drawing

/** A soft shadow under a prop. */
const shadow = (/** @type {string} */ shape) =>
  `<g transform="translate(3 5)" opacity=".12" filter="url(#lv-soft)">${shape}</g>`;

const MITTEN = "M-16 -10C-6 -16 10 -15 17 -8C22 -3 22 6 16 10C8 15 -8 15 -16 9C-21 5 -21 -6 -16 -10Z";

/** A resting hand, palm down, knuckles toward +x. */
const restHand = (/** @type {string} */ skin) => `
        <path d="${MITTEN}" fill="${skin}" stroke="${INK_SOFT}" stroke-width="1.6"/>
        <path d="M4 -12V-2M10 -11V-1M15 -8V0" stroke="${INK_SOFT}" stroke-width="1.1" stroke-linecap="round" opacity=".5"/>`;

/** A hand, palm down, knuckles toward +x. Grips swap the finger shapes. */
const handShape = (/** @type {string} */ skin, /** @type {string} */ who) => `
        <g data-grip="rest">
          <path d="M-16 -10C-6 -16 10 -15 17 -8C22 -3 22 6 16 10C8 15 -8 15 -16 9C-21 5 -21 -6 -16 -10Z" fill="${skin}" stroke="${INK_SOFT}" stroke-width="1.6"/>
          <path d="M4 -12V-2M10 -11V-1M15 -8V0" stroke="${INK_SOFT}" stroke-width="1.1" stroke-linecap="round" opacity=".5"/>
        </g>
        <g data-grip="open" opacity="0">
          <path d="M-16 -6C-8 -12 14 -14 20 -6C24 0 20 8 12 10C2 12 -10 12 -16 7C-20 3 -20 -3 -16 -6Z" fill="${skin}" stroke="${INK_SOFT}" stroke-width="1.6"/>
          <path d="M6 -10L12 -20M12 -9L19 -17M16 -6L24 -12" stroke="${INK_SOFT}" stroke-width="1.6" stroke-linecap="round"/>
          <path d="M-2 -10L-4 -20" stroke="${INK_SOFT}" stroke-width="1.6" stroke-linecap="round"/>
        </g>
        <g data-grip="point" opacity="0">
          <path d="M-16 -9C-6 -14 6 -13 11 -8C15 -4 15 5 10 9C3 13 -9 13 -16 8C-20 4 -20 -6 -16 -9Z" fill="${skin}" stroke="${INK_SOFT}" stroke-width="1.6"/>
          <path d="M8 -6L30 -8C33 -8 33 -2 30 -2L11 0" fill="${skin}" stroke="${INK_SOFT}" stroke-width="1.6" stroke-linejoin="round"/>
        </g>
        ${
          who === "erlich"
            ? `<g data-grip="phone" opacity="0">
          <path d="M-16 -10C-6 -16 10 -15 17 -8C22 -3 22 6 16 10C8 15 -8 15 -16 9C-21 5 -21 -6 -16 -10Z" fill="${skin}" stroke="${INK_SOFT}" stroke-width="1.6"/>
          <path d="M-4 -14C-2 -20 6 -20 8 -14" fill="${skin}" stroke="${INK_SOFT}" stroke-width="1.4"/>
        </g>
        <g data-grip="stamp" opacity="0">
          <rect x="-8" y="-46" width="16" height="30" rx="5" fill="${WOOD}" stroke="${INK_SOFT}" stroke-width="1.4"/>
          <rect x="-18" y="-18" width="36" height="12" rx="3" fill="${RUST}"/>
          <path d="M-16 -10C-6 -16 10 -15 17 -8C22 -3 22 6 16 10C8 15 -8 15 -16 9C-21 5 -21 -6 -16 -10Z" fill="${skin}" stroke="${INK_SOFT}" stroke-width="1.6" transform="translate(0 -30)"/>
        </g>`
            : `<g data-grip="hold" opacity="0">
          <path d="M-16 -9C-6 -14 8 -14 14 -8C18 -2 16 7 10 10C2 14 -10 13 -16 8C-20 3 -20 -5 -16 -9Z" fill="${skin}" stroke="${INK_SOFT}" stroke-width="1.6"/>
          <path d="M6 -10C10 -8 12 -2 9 3M11 -8C15 -5 16 1 13 5" stroke="${INK_SOFT}" stroke-width="1.2" fill="none" opacity=".6"/>
        </g>`
        }`;

/** Erlich: big curly hair, beard and mustache, heavy-lidded eyes, brown quarter-zip. */
function erlichMarkup() {
  const hairBack = `${scallops(226, 256, 98, 92, 150, 390, 15, 0.4)}L300 330L150 330Z`;
  const fringeEdge = scallops(236, 238, 54, 7, 0, 180, 7, 0.1);
  const fringe = `M182 238Q236 192 290 236${fringeEdge.replace(/^M[^A]+/, "")}Z`;
  const beard = `M287 282C286 300 285 316 283 329${scallops(236, 316, 50, 38, 20, 160, 8, 0.3).replace(/^M[^A]+/, "")}C188 316 185 300 184 284C192 291 214 300 232 301C250 301 270 296 287 282Z`;
  return `
    <g data-erlich>
      <path d="M86 470C86 430 96 404 132 388C160 376 190 362 212 352L262 350C290 360 322 374 350 388C380 402 392 432 392 470Z" fill="${E_SWEATER}" stroke="${INK}" stroke-width="2.2"/>
      <path d="M132 388C120 410 112 440 110 470" fill="none" stroke="${E_SWEATER_DARK}" stroke-width="10" opacity=".5"/>
      <path d="M212 352L262 350L250 380L238 372L224 380Z" fill="${E_SHIRT}" stroke="${INK}" stroke-width="1.8" stroke-linejoin="round"/>
      <path d="M200 350C212 342 262 340 276 348L272 366C258 360 214 360 204 366Z" fill="${E_SWEATER_DARK}" stroke="${INK}" stroke-width="1.8"/>
      <path d="M238 368V430" stroke="${INK_SOFT}" stroke-width="2"/>
      <rect x="234" y="384" width="8" height="12" rx="2" fill="${INK_SOFT}"/>
      <g data-erlich-sticker opacity="0" transform="translate(206 418) rotate(-8)">
        <rect x="-38" y="-13" width="76" height="26" rx="4" fill="${PAPER}" stroke="${RUST}" stroke-width="2"/>
        ${text(`x="0" y="5.5" text-anchor="middle" fill="${RUST}" font-size="14" font-weight="700" letter-spacing="1"`, "BOWLBOT")}
      </g>
      <g data-erlich-head>
        <path d="${hairBack}" fill="${E_HAIR}" stroke="${INK}" stroke-width="2.2" stroke-linejoin="round"/>
        <path d="M150 230C156 236 160 246 158 254M168 196C176 200 180 210 178 218M296 210C292 218 292 228 296 236M300 262C296 270 298 280 302 286M206 170C214 172 220 180 220 186M250 168C246 176 248 184 254 188" fill="none" stroke="${E_HAIR_DARK}" stroke-width="2.4" stroke-linecap="round"/>
        <ellipse cx="236" cy="282" rx="50" ry="60" fill="${E_SKIN}" stroke="${INK}" stroke-width="2.2"/>
        <path d="${fringe}" fill="${E_HAIR}"/>
        <path d="${fringeEdge}" fill="none" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
        <g data-erlich-brows>
          <path data-erlich-brow="0" d="M202 251Q214 246 226 249" fill="none" stroke="${E_HAIR_DARK}" stroke-width="5" stroke-linecap="round"/>
          <path data-erlich-brow="1" d="M246 248Q258 245 270 250" fill="none" stroke="${E_HAIR_DARK}" stroke-width="5" stroke-linecap="round"/>
        </g>
        <g>
          <ellipse cx="215" cy="268" rx="12" ry="9.5" fill="${PAPER}" stroke="${INK}" stroke-width="1.6"/>
          <ellipse cx="257" cy="267" rx="11" ry="9" fill="${PAPER}" stroke="${INK}" stroke-width="1.6"/>
          <g data-erlich-irises>
            <circle cx="216" cy="268" r="5.6" fill="#3a2a1e"/>
            <circle cx="258" cy="267" r="5.4" fill="#3a2a1e"/>
            <circle cx="218" cy="266" r="1.6" fill="${PAPER}"/>
            <circle cx="260" cy="265" r="1.5" fill="${PAPER}"/>
          </g>
          <path data-erlich-lid="0" fill="${E_SKIN}" stroke="${INK}" stroke-width="1.6"/>
          <path data-erlich-lid="1" fill="${E_SKIN}" stroke="${INK}" stroke-width="1.6"/>
          <path d="M205 280Q215 284 225 280M248 279Q257 283 266 279" fill="none" stroke="${INK}" stroke-width="1.1" opacity=".45"/>
        </g>
        <path d="M240 268C242 280 248 288 249 295C245 300 237 300 233 296" fill="#e4bb9c" stroke="${INK}" stroke-width="1.8" stroke-linecap="round"/>
        <path d="${beard}" fill="${E_HAIR}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
        <path d="M208 312C218 300 232 299 242 305C252 299 266 301 276 311C266 314 254 313 242 309C230 314 218 315 208 312Z" fill="${E_HAIR_DARK}" stroke="${INK}" stroke-width="1.6"/>
        <path data-erlich-mouth fill="none" stroke="${INK}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
      </g>
      <path d="M132 392Q118 432 146 476" fill="none" stroke="${E_SWEATER}" stroke-width="34" stroke-linecap="round"/>
      <g transform="translate(160 478) rotate(10)">${restHand(E_SKIN)}</g>
    </g>`;
}

/** Jian-Yang: black bowl cut, rectangular black glasses, red tracksuit with white stripes. */
function jyMarkup() {
  return `
    <g data-jy>
      <path d="M632 470C632 434 640 410 668 398C690 388 708 372 716 364L756 364C766 374 784 388 806 398C836 410 846 434 846 470Z" fill="${J_JACKET}" stroke="${INK}" stroke-width="2.2"/>
      <path d="M716 364C694 378 674 392 664 404M756 364C778 378 800 392 812 404" fill="none" stroke="${PAPER}" stroke-width="3.2" stroke-linecap="round"/>
      <path d="M720 364L736 386L752 364Z" fill="${PAPER}" stroke="${INK}" stroke-width="1.6" stroke-linejoin="round"/>
      <path d="M708 360C716 352 756 352 764 360L760 372C750 366 722 366 712 372Z" fill="${J_JACKET_DARK}" stroke="${INK}" stroke-width="1.8"/>
      <path d="M710 368C720 364 752 364 762 368" fill="none" stroke="${PAPER}" stroke-width="1.6"/>
      <path d="M736 388V470" stroke="${INK_SOFT}" stroke-width="2"/>
      <g data-jy-head>
        <path d="M684 304C680 262 700 230 736 228C772 226 794 258 792 304C786 312 780 316 776 316L776 290L696 290L696 316C690 314 686 310 684 304Z" fill="${J_HAIR}" stroke="${INK}" stroke-width="2.2" stroke-linejoin="round"/>
        <ellipse cx="734" cy="298" rx="44" ry="54" fill="${J_SKIN}" stroke="${INK}" stroke-width="2.2"/>
        <path d="M688 284C686 252 706 236 736 235C766 234 786 252 786 284C780 278 774 274 770 274L766 268L758 274L748 268L740 275L730 268L722 275L712 269L704 276C698 276 692 280 688 284Z" fill="${J_HAIR}" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/>
        <path d="M688 284C688 296 690 306 694 314L700 314L698 288Z M786 284C786 296 784 306 780 314L774 314L776 288Z" fill="${J_HAIR}"/>
        <g data-jy-brows>
          <path data-jy-brow="0" d="M700 279L718 278" stroke="${J_HAIR}" stroke-width="3.2" stroke-linecap="round"/>
          <path data-jy-brow="1" d="M744 278L762 279" stroke="${J_HAIR}" stroke-width="3.2" stroke-linecap="round"/>
        </g>
        <g>
          <ellipse cx="710" cy="293" rx="6" ry="3.6" fill="${PAPER}"/>
          <ellipse cx="752" cy="292" rx="5.6" ry="3.4" fill="${PAPER}"/>
          <g data-jy-irises>
            <circle cx="709" cy="293" r="3.1" fill="${J_HAIR}"/>
            <circle cx="751" cy="292" r="3" fill="${J_HAIR}"/>
          </g>
          <path data-jy-lid="0" fill="${J_SKIN}" stroke="${INK}" stroke-width="1.3"/>
          <path data-jy-lid="1" fill="${J_SKIN}" stroke="${INK}" stroke-width="1.3"/>
        </g>
        <g fill="${PAPER}" fill-opacity=".14" stroke="${INK}" stroke-width="3.6" stroke-linejoin="round">
          <rect x="693" y="283" width="34" height="21" rx="3"/>
          <rect x="737" y="282" width="32" height="21" rx="3"/>
        </g>
        <path d="M727 290L737 290M769 289L784 286" stroke="${INK}" stroke-width="3.2" stroke-linecap="round"/>
        <path d="M734 300C733 308 729 313 726 316C729 318 734 318 737 317" fill="none" stroke="${INK}" stroke-width="1.7" stroke-linecap="round"/>
        <path data-jy-mouth fill="none" stroke="${INK}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
      </g>
      <path d="M816 404Q836 440 806 476" fill="none" stroke="${J_JACKET}" stroke-width="28" stroke-linecap="round"/>
      <path d="M826 406Q846 440 818 474" fill="none" stroke="${PAPER}" stroke-width="2.6" stroke-linecap="round"/>
      <g transform="translate(796 478) rotate(170) scale(1 -1)">${restHand(J_SKIN)}</g>
    </g>`;
}

/** The thatch fringe under the palapa's roof. */
const palapaFringe = () => {
  const points = ["M-48 -3"];
  for (let i = 0; i < 16; i++) {
    const x = -48 + i * 6;
    points.push(`L${x + 3} ${i % 2 ? 6 : 5}`, `L${x + 6} -1`);
  }
  points.push("L48 -3Z");
  return points.join("");
};

/** The scene's markup: the drawing, and the two speech bubbles over it. */
export function sceneMarkup() {
  const slots = Array.from({ length: FLAP_SLOTS }, (_, i) => {
    const x = -66 + i * 22;
    return `
          <g data-flap="${i}" transform="translate(${x} 0)">
            <rect x="-9" y="-15" width="18" height="30" rx="2" fill="#2b2621"/>
            <g data-flap-face>
              ${text(`x="0" y="7.5" text-anchor="middle" fill="${IVORY}" font-size="20" font-weight="600"`, " ")}
            </g>
            <path d="M-9 0.5H9" stroke="#14120f" stroke-width="1.1"/>
          </g>`;
  }).join("");

  const steam = ["M-14 -18C-20 -26 -8 -32 -14 -42", "M0 -20C-6 -28 6 -34 0 -46", "M14 -18C8 -26 20 -32 14 -42"]
    .map((d) => `<path d="${d}"/>`)
    .join("");

  return `<div data-scene>
  <svg viewBox="0 ${VIEW_TOP} ${SCENE_WIDTH} ${SCENE_HEIGHT - VIEW_TOP}" preserveAspectRatio="xMidYMax slice" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
  <defs>
    <filter id="lv-soft" x="-30%" y="-30%" width="160%" height="170%"><feGaussianBlur stdDeviation="4"/></filter>
  </defs>
  <g data-world data-layout-allow-overflow>
    <rect x="-400" y="-700" width="1760" height="1500" fill="#f1ebdf"/>
    <path d="M-400 440H1360" stroke="#e4dccb" stroke-width="2"/>

    ${erlichMarkup()}
    ${jyMarkup()}

    <g data-cloud opacity="0">
      <circle cx="322" cy="270" r="6" fill="${PAPER}" stroke="${INK}" stroke-width="1.8"/>
      <circle cx="346" cy="258" r="9" fill="${PAPER}" stroke="${INK}" stroke-width="1.8"/>
      <g transform="translate(494 254)">
        <path d="${scallops(0, 0, 106, 62, 0, 360, 16, 0.6)}Z" fill="${PAPER}" stroke="${INK}" stroke-width="2"/>
        <path d="M-80 40H82M-80 40V-40" fill="none" stroke="${INK}" stroke-opacity=".25" stroke-width="1.5"/>
        <g data-cloud-button opacity="0" transform="translate(-66 -30)">
          <rect x="-15" y="2" width="30" height="9" rx="3" fill="${MUTED}"/>
          <g data-cloud-button-top><ellipse cx="0" cy="2" rx="11" ry="6" fill="${TERRACOTTA}" stroke="${INK}" stroke-width="1.5"/></g>
        </g>
        <g data-no-humans opacity="0" transform="translate(-30 -34)" fill="none" stroke="${MUTED}" stroke-width="2">
          <circle cx="0" cy="-5" r="4"/>
          <path d="M-7 8C-7 2 7 2 7 8"/>
          <circle cx="0" cy="1" r="12" stroke="${RUST}"/>
          <path d="M-8 -8L8 10" stroke="${RUST}"/>
        </g>
        <g data-money opacity="0" fill="${RUST}">
          ${text(`x="40" y="-22" font-size="18" font-weight="700"`, "$")}
          ${text(`x="58" y="-4" font-size="14" font-weight="700"`, "$")}
          ${text(`x="26" y="-4" font-size="12" font-weight="700"`, "$")}
        </g>
        <path data-bowl-fill fill="#f3ebdc" opacity="0"/>
        <path data-line fill="none" stroke="${TERRACOTTA}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" pathLength="1" stroke-dasharray="1 2" stroke-dashoffset="1"/>
        <ellipse data-rim cx="0" cy="-6" rx="52" ry="8" fill="none" stroke="${TERRACOTTA}" stroke-width="3" opacity="0"/>
        <g data-bull>
          <path d="M-9 -2C-9 -10 -5 -13 0 -13C5 -13 9 -10 9 -2C9 6 5 10 0 10C-5 10 -9 6 -9 -2Z" fill="${INK}"/>
          <path d="M-8 -9C-14 -12 -17 -18 -16 -24M8 -9C14 -12 17 -18 16 -24" fill="none" stroke="${INK}" stroke-width="3" stroke-linecap="round"/>
          <circle cx="-3.5" cy="4" r="1.5" fill="${TERRACOTTA}"/>
          <circle cx="3.5" cy="4" r="1.5" fill="${TERRACOTTA}"/>
        </g>
      </g>
    </g>

    <rect x="-400" y="440" width="1760" height="100" fill="${TABLE_TOP}"/>
    <rect x="-400" y="540" width="1760" height="260" fill="${TABLE_FRONT}"/>
    <path d="M-400 440H1360M-400 540H1360" stroke="#cfc2aa" stroke-width="2"/>

    <g data-prop="appliance" transform="translate(586 446)">
      ${shadow(`<rect x="-80" y="-124" width="160" height="124" rx="10" fill="${INK}"/>`)}
      <g data-bowlbot>
        <rect x="-78" y="-122" width="156" height="122" rx="10" fill="${PAPER}" stroke="${INK}" stroke-width="2.2"/>
        <rect x="-78" y="-122" width="156" height="34" rx="10" fill="#efe7d8" stroke="${INK}" stroke-width="2.2"/>
        <rect x="-66" y="-80" width="132" height="72" rx="6" fill="none" stroke="${INK}" stroke-width="1.8"/>
        <rect x="-40" y="-74" width="80" height="7" rx="3.5" fill="${LINE}" stroke="${INK}" stroke-width="1.2"/>
        <circle data-bowlbot-light cx="64" cy="-105" r="4" fill="${TERRACOTTA}"/>
        <g data-arm transform="translate(-78 -70)">
          <path d="M0 0L-26 10L-40 38" fill="none" stroke="${INK}" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>
          <circle cx="-26" cy="10" r="4.5" fill="${PAPER}" stroke="${INK}" stroke-width="2"/>
          <rect x="-54" y="36" width="28" height="12" rx="4" fill="${TERRACOTTA}" stroke="${INK}" stroke-width="1.6"/>
          <path d="M-50 48V54M-44 48V54M-38 48V54M-32 48V54" stroke="${INK}" stroke-width="1.4"/>
        </g>
      </g>
      <g data-cloth>
        <path d="M-90 2C-92 -40 -88 -90 -80 -122C-60 -136 60 -136 80 -122C88 -90 92 -40 90 2C60 6 -60 6 -90 2Z" fill="#1b1814" stroke="#3a342c" stroke-width="2"/>
        <path d="M-40 -126C-46 -90 -50 -40 -52 2M30 -128C36 -90 40 -40 42 3" fill="none" stroke="#3a342c" stroke-width="2"/>
        <rect x="-62" y="-118" width="124" height="22" rx="3" fill="${IVORY}"/>
        ${text(`x="0" y="-102" text-anchor="middle" fill="${INK}" font-size="13" font-weight="600"`, "Project Leviathan")}
      </g>
      <g data-notify opacity="0" transform="translate(-30 -150)">
        ${shadow(`<rect x="-86" y="-24" width="172" height="48" rx="12" fill="${INK}"/>`)}
        <rect x="-86" y="-24" width="172" height="48" rx="12" fill="${PAPER}" stroke="${INK}" stroke-width="1.6"/>
        <circle cx="-68" cy="-8" r="5" fill="${TERRACOTTA}"/>
        ${text(`x="-56" y="-3" fill="${MUTED}" font-size="12" font-weight="600"`, "BowlBot")}
        ${text(`x="-74" y="15" fill="${INK}" font-size="14" font-weight="600"`, "Erlich, clean the sink.")}
        <path d="M18 24L28 34L36 24" fill="${PAPER}" stroke="${INK}" stroke-width="1.6" stroke-linejoin="round"/>
        <path d="M19 23H35" stroke="${PAPER}" stroke-width="3"/>
      </g>
      <g data-flaps transform="translate(0 -52)">${slots}
      </g>
      <g data-tag opacity="0" transform="translate(40 -30)">
        <g transform="rotate(8)">
          ${shadow(`<path d="M-26 -12H20L30 0L20 12H-26Z" fill="${INK}"/>`)}
          <path d="M-30 -12H22L32 0L22 12H-30Z" fill="${PAPER}" stroke="${LINE}" stroke-width="1.5" stroke-linejoin="round"/>
          <circle cx="-22" cy="0" r="2.6" fill="${IVORY}" stroke="${LINE}"/>
          <g data-tag-cheap>${text(`x="2" y="5.5" text-anchor="middle" fill="${RUST}" font-size="15" font-weight="700" letter-spacing="1"`, "$$$")}</g>
          <g data-tag-price opacity="0">${text(`x="2" y="5" text-anchor="middle" fill="${RUST}" font-size="13" font-weight="700"`, "$14,000")}</g>
        </g>
      </g>
    </g>

    <g data-prop="palapa">
      ${shadow(`<path d="M-48 -2L0 -38L48 -2ZM-40 26H40V35H-40Z" fill="${INK}"/>`)}
      <rect x="-40" y="26" width="80" height="9" rx="3" fill="#e6d6b4" stroke="#cdbb92"/>
      <rect x="-31" y="-4" width="6" height="32" rx="1.5" fill="${WOOD}"/>
      <rect x="25" y="-4" width="6" height="32" rx="1.5" fill="${WOOD}"/>
      <rect x="-3" y="-4" width="6" height="32" rx="1.5" fill="#7a5c3e"/>
      <path d="${palapaFringe()}" fill="${STRAW}" stroke="${STRAW_DARK}" stroke-width="1.2" stroke-linejoin="round"/>
      <path d="M-48 -2L0 -38L48 -2Z" fill="${STRAW}" stroke="${STRAW_DARK}" stroke-width="1.5" stroke-linejoin="round"/>
      <path d="M0 -38L-34 -2M0 -38L-17 -2M0 -38V-2M0 -38L17 -2M0 -38L34 -2" stroke="${STRAW_DARK}" stroke-width="1.2" opacity=".7"/>
      <g transform="translate(0 52) rotate(-3)">
        <rect x="-28" y="-11" width="56" height="22" rx="4" fill="${PAPER}" stroke="${LINE}"/>
        ${text(`x="0" y="5" text-anchor="middle" fill="${INK}" font-size="12" font-weight="600"`, "Palapa")}
      </g>
    </g>

    <g data-prop="terms" opacity="0">
      ${shadow(`<path d="M-70 -24H74L84 24H-80Z" fill="${INK}"/>`)}
      <path d="M-70 -24H74L84 24H-80Z" fill="${PAPER}" stroke="${LINE}" stroke-width="1.5" stroke-linejoin="round"/>
      ${text(`x="14" y="-6" text-anchor="middle" fill="${INK}" font-size="15" font-weight="600"`, "Term sheet")}
      <path d="M-26 10H60M-30 18H62" stroke="${INK}" stroke-opacity=".25"/>
      <g data-chip opacity="0" transform="translate(-50 6)">
        <ellipse rx="19" ry="12" fill="${PAPER}" stroke="${TERRACOTTA}" stroke-width="2"/>
        ${text(`x="0" y="4.5" text-anchor="middle" fill="${INK}" font-size="12" font-weight="700"`, "10%")}
      </g>
      <g data-stamp opacity="0" transform="translate(30 8) rotate(-6)">
        <rect x="-36" y="-12" width="72" height="24" rx="4" fill="none" stroke="${TERRACOTTA}" stroke-width="2.6"/>
        ${text(`x="0" y="6.5" text-anchor="middle" fill="${RUST}" font-size="17" font-weight="700" letter-spacing="2"`, "DEAL")}
      </g>
    </g>

    <g data-prop="bowl" opacity="0">
      ${shadow(`<path d="M-44 -6C-44 18 -24 32 0 32C24 32 44 18 44 -6Z" fill="${INK}"/>`)}
      <path d="M-44 -6C-44 18 -24 32 0 32C24 32 44 18 44 -6Z" fill="#f3ebdc" stroke="${TERRACOTTA}" stroke-width="3"/>
      <ellipse cx="0" cy="-6" rx="44" ry="7" fill="#efe4cf" stroke="${TERRACOTTA}" stroke-width="3"/>
      <g fill="#a88a6a"><circle cx="-18" cy="8" r="3.4"/><circle cx="6" cy="14" r="4"/><circle cx="22" cy="4" r="2.6"/><circle cx="-4" cy="22" r="2.4"/></g>
      <g data-steam fill="none" stroke="#b3a995" stroke-width="2.2" stroke-linecap="round" opacity="0">${steam}</g>
    </g>

    <g data-prop="sponge">
      ${shadow(`<rect x="-21" y="-13" width="42" height="26" rx="6" fill="${INK}"/>`)}
      <rect x="-21" y="-13" width="42" height="26" rx="6" fill="#e9c46a"/>
      <rect x="-21" y="-13" width="42" height="10" rx="5" fill="#7a9a6a"/>
      <circle cx="-9" cy="6" r="1.8" fill="#c9a24a"/>
      <circle cx="3" cy="3" r="1.5" fill="#c9a24a"/>
      <circle cx="12" cy="7" r="1.8" fill="#c9a24a"/>
    </g>

    <g data-phone opacity="0">
      <rect x="-24" y="-44" width="48" height="88" rx="9" fill="#2b2621" stroke="${INK}" stroke-width="1.5"/>
      <rect x="-19" y="-37" width="38" height="74" rx="4" fill="${PAPER}"/>
      <rect x="-8" y="-27" width="16" height="13" rx="2.5" fill="${TERRACOTTA}"/>
      <path d="M-5 -27V-30C-5 -36 5 -36 5 -30V-27" fill="none" stroke="${TERRACOTTA}" stroke-width="2.4"/>
      ${text(`x="0" y="5" text-anchor="middle" fill="${INK}" font-size="17" font-weight="600"`, "TWT")}
      <rect x="-13" y="13" width="26" height="2.5" rx="1" fill="${LINE}"/>
      <rect x="-10" y="20" width="20" height="2.5" rx="1" fill="${LINE}"/>
    </g>
    <g data-prop="fish" opacity="0">
      ${shadow(`<ellipse rx="32" ry="11" fill="${INK}"/>`)}
      <ellipse rx="32" ry="11" fill="${PAPER}" stroke="${LINE}" stroke-width="1.5"/>
      <ellipse rx="24" ry="7" fill="none" stroke="${LINE}"/>
      <g fill="none" stroke="${MUTED}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
        <path d="M-20 -3L-12 0L-20 3Z" fill="${PAPER}"/>
        <path d="M-12 0H14"/>
        <path d="M-6 -4L-4 4M0 -4L2 4M6 -4L8 4"/>
        <path d="M14 0L21 -5V5Z" fill="${PAPER}"/>
      </g>
      <circle cx="17" cy="-1" r="1.2" fill="${INK}"/>
    </g>
    <path data-erlich-arm fill="none" stroke="${E_SWEATER}" stroke-width="34" stroke-linecap="round"/>
    <g data-erlich-hand>${handShape(E_SKIN, "erlich")}</g>
    <path data-jy-arm fill="none" stroke="${J_JACKET}" stroke-width="28" stroke-linecap="round"/>
    <path data-jy-stripe fill="none" stroke="${PAPER}" stroke-width="2.6" stroke-linecap="round"/>
    <g data-jy-hand>${handShape(J_SKIN, "jian_yang")}</g>
    <g data-flying-sticker opacity="0">
      <rect x="-38" y="-13" width="76" height="26" rx="4" fill="${PAPER}" stroke="${RUST}" stroke-width="2"/>
      ${text(`x="0" y="5.5" text-anchor="middle" fill="${RUST}" font-size="14" font-weight="700" letter-spacing="1"`, "BOWLBOT")}
    </g>
  </g>
  </svg>
  <div data-bubbles aria-hidden="true">
    <p data-bubble="erlich"><span data-bubble-text></span></p>
    <p data-bubble="jian_yang"><span data-bubble-text></span></p>
  </div>
</div>`;
}

/**
 * @typedef {{ root: Element, grips: Record<string, Element> }} HandParts
 * @typedef {{
 *   world: SVGGElement,
 *   erlich: SVGGElement, erlichHead: SVGGElement, erlichMouth: SVGPathElement, erlichBrows: SVGPathElement[],
 *   erlichLids: SVGPathElement[], erlichIrises: SVGGElement, erlichArm: SVGPathElement, erlichHand: HandParts,
 *   erlichSticker: SVGGElement,
 *   jy: SVGGElement, jyHead: SVGGElement, jyMouth: SVGPathElement, jyBrows: SVGPathElement[],
 *   jyLids: SVGPathElement[], jyIrises: SVGGElement, jyArm: SVGPathElement, jyStripe: SVGPathElement, jyHand: HandParts,
 *   cloud: SVGGElement, cloudButton: SVGGElement, cloudButtonTop: SVGGElement, noHumans: SVGGElement, money: SVGGElement, line: SVGPathElement, rim: SVGEllipseElement,
 *   bowlFill: SVGPathElement, bull: SVGGElement,
 *   appliance: SVGGElement, cloth: SVGGElement, arm: SVGGElement, light: SVGCircleElement,
 *   flaps: Array<{ face: SVGGElement, label: SVGTextElement }>, flapRow: SVGGElement,
 *   tag: SVGGElement, tagCheap: SVGGElement, tagPrice: SVGGElement,
 *   palapa: SVGGElement, terms: SVGGElement, chip: SVGGElement, stamp: SVGGElement, phone: SVGGElement,
 *   bowl: SVGGElement, steam: SVGGElement, sponge: SVGGElement, fish: SVGGElement, notify: SVGGElement, flyingSticker: SVGGElement,
 *   bubbles: Record<"erlich" | "jian_yang", { box: HTMLElement, text: HTMLElement }>,
 *   shown: { page: number, flaps: string[] },
 * }} SceneParts
 */

/**
 * Finds the animated parts of a mounted scene.
 * @param {ParentNode} root
 * @returns {SceneParts}
 */
export function bindScene(root) {
  /** @param {string} selector @param {ParentNode} [within] */
  const one = (selector, within = root) => {
    const element = within.querySelector(selector);
    if (!element) throw new Error(`Scene part missing: ${selector}`);
    return element;
  };
  /** @param {string} selector */
  const all = (selector) => Array.from(root.querySelectorAll(selector));
  /** @param {string} selector */
  const hand = (selector) => {
    const handRoot = one(selector);
    /** @type {Record<string, Element>} */
    const grips = {};
    handRoot.querySelectorAll("[data-grip]").forEach((g) => {
      grips[g.getAttribute("data-grip") ?? ""] = g;
    });
    return { root: handRoot, grips };
  };
  const flaps = all("[data-flap]").map((slot) => {
    const face = slot.querySelector("[data-flap-face]");
    const label = slot.querySelector("text");
    if (!face || !label) throw new Error("Scene part missing: flap");
    return { face: /** @type {SVGGElement} */ (face), label: /** @type {SVGTextElement} */ (label) };
  });
  /** @param {"erlich" | "jian_yang"} who */
  const bubble = (who) => {
    const box = /** @type {HTMLElement} */ (one(`[data-bubble="${who}"]`));
    return { box, text: /** @type {HTMLElement} */ (one("[data-bubble-text]", box)) };
  };
  return {
    world: /** @type {SVGGElement} */ (one("[data-world]")),
    erlich: /** @type {SVGGElement} */ (one("[data-erlich]")),
    erlichHead: /** @type {SVGGElement} */ (one("[data-erlich-head]")),
    erlichMouth: /** @type {SVGPathElement} */ (one("[data-erlich-mouth]")),
    erlichBrows: /** @type {SVGPathElement[]} */ (all("[data-erlich-brow]")),
    erlichLids: /** @type {SVGPathElement[]} */ (all("[data-erlich-lid]")),
    erlichIrises: /** @type {SVGGElement} */ (one("[data-erlich-irises]")),
    erlichArm: /** @type {SVGPathElement} */ (one("[data-erlich-arm]")),
    erlichHand: hand("[data-erlich-hand]"),
    erlichSticker: /** @type {SVGGElement} */ (one("[data-erlich-sticker]")),
    jy: /** @type {SVGGElement} */ (one("[data-jy]")),
    jyHead: /** @type {SVGGElement} */ (one("[data-jy-head]")),
    jyMouth: /** @type {SVGPathElement} */ (one("[data-jy-mouth]")),
    jyBrows: /** @type {SVGPathElement[]} */ (all("[data-jy-brow]")),
    jyLids: /** @type {SVGPathElement[]} */ (all("[data-jy-lid]")),
    jyIrises: /** @type {SVGGElement} */ (one("[data-jy-irises]")),
    jyArm: /** @type {SVGPathElement} */ (one("[data-jy-arm]")),
    jyStripe: /** @type {SVGPathElement} */ (one("[data-jy-stripe]")),
    jyHand: hand("[data-jy-hand]"),
    cloud: /** @type {SVGGElement} */ (one("[data-cloud]")),
    cloudButton: /** @type {SVGGElement} */ (one("[data-cloud-button]")),
    cloudButtonTop: /** @type {SVGGElement} */ (one("[data-cloud-button-top]")),
    noHumans: /** @type {SVGGElement} */ (one("[data-no-humans]")),
    money: /** @type {SVGGElement} */ (one("[data-money]")),
    line: /** @type {SVGPathElement} */ (one("[data-line]")),
    rim: /** @type {SVGEllipseElement} */ (one("[data-rim]")),
    bowlFill: /** @type {SVGPathElement} */ (one("[data-bowl-fill]")),
    bull: /** @type {SVGGElement} */ (one("[data-bull]")),
    appliance: /** @type {SVGGElement} */ (one('[data-prop="appliance"]')),
    cloth: /** @type {SVGGElement} */ (one("[data-cloth]")),
    arm: /** @type {SVGGElement} */ (one("[data-arm]")),
    light: /** @type {SVGCircleElement} */ (one("[data-bowlbot-light]")),
    flaps,
    flapRow: /** @type {SVGGElement} */ (one("[data-flaps]")),
    tag: /** @type {SVGGElement} */ (one("[data-tag]")),
    tagCheap: /** @type {SVGGElement} */ (one("[data-tag-cheap]")),
    tagPrice: /** @type {SVGGElement} */ (one("[data-tag-price]")),
    palapa: /** @type {SVGGElement} */ (one('[data-prop="palapa"]')),
    phone: /** @type {SVGGElement} */ (one("[data-phone]")),
    terms: /** @type {SVGGElement} */ (one('[data-prop="terms"]')),
    chip: /** @type {SVGGElement} */ (one("[data-chip]")),
    stamp: /** @type {SVGGElement} */ (one("[data-stamp]")),
    bowl: /** @type {SVGGElement} */ (one('[data-prop="bowl"]')),
    steam: /** @type {SVGGElement} */ (one("[data-steam]")),
    sponge: /** @type {SVGGElement} */ (one('[data-prop="sponge"]')),
    fish: /** @type {SVGGElement} */ (one('[data-prop="fish"]')),
    notify: /** @type {SVGGElement} */ (one("[data-notify]")),
    flyingSticker: /** @type {SVGGElement} */ (one("[data-flying-sticker]")),
    bubbles: { erlich: bubble("erlich"), jian_yang: bubble("jian_yang") },
    shown: { page: -2, flaps: [] },
  };
}

/**
 * The flip-letter display at time t: the character in each slot and how open
 * its flap is (1 fully visible, 0 edge-on). A slot whose letter does not
 * change stays still.
 * @param {number} t
 */
function flapsAt(t) {
  return Array.from({ length: FLAP_SLOTS }, (_, i) => {
    let char = FLAP_WORDS[0]?.[1][i] ?? " ";
    let open = 1;
    for (let w = 1; w < FLAP_WORDS.length; w++) {
      const word = FLAP_WORDS[w];
      const before = FLAP_WORDS[w - 1];
      if (!word || !before) continue;
      const from = before[1][i] ?? " ";
      const to = word[1][i] ?? " ";
      const start = word[0] + i * 0.04;
      const p = (t - start) / 0.2;
      if (p < 0) break;
      if (from === to) continue;
      if (p >= 1) {
        char = to;
        open = 1;
        continue;
      }
      char = p < 0.5 ? from : to;
      open = Math.abs(1 - 2 * p);
    }
    return { char, open };
  });
}

/**
 * The upper lid over an eye: the eye's outline from corner to corner, lowered
 * by the lid amount.
 * @param {number} cx
 * @param {number} cy
 * @param {number} rx
 * @param {number} ry
 * @param {number} lid 0 open, 1 closed
 */
function lidPath(cx, cy, rx, ry, lid) {
  const upper = cy - 2 * ry - 3;
  const lower = cy - 2 * ry + 4 * ry * lid;
  return `M${f(cx - rx - 1.5)} ${f(cy)}Q${f(cx)} ${f(upper)} ${f(cx + rx + 1.5)} ${f(cy)}Q${f(cx)} ${f(lower)} ${f(cx - rx - 1.5)} ${f(cy)}Z`;
}

/**
 * Sets a hand's position, angle and grip.
 * @param {HandParts} hand
 * @param {number} x
 * @param {number} y
 * @param {number} angle
 * @param {string} grip
 * @param {boolean} mirror
 */
function placeHand(hand, x, y, angle, grip, mirror) {
  hand.root.setAttribute("transform", `translate(${f(x)} ${f(y)}) rotate(${f(angle)})${mirror ? " scale(1 -1)" : ""}`);
  for (const [name, element] of Object.entries(hand.grips)) {
    element.setAttribute("opacity", name === grip ? "1" : "0");
  }
}

/**
 * Draws the scene at time t (seconds of the sound film). wall is a clock that
 * keeps running while the muted view waits on a bubble.
 * @param {SceneParts} scene
 * @param {number} time
 * @param {number} [wall]
 */
export function renderScene(scene, time, wall = time) {
  const t = Math.min(SCENE_DURATION, Math.max(0, time));
  const zoom = cameraZoom(t);
  const cx = cameraX(t);
  const cy = cameraY(t);
  scene.world.setAttribute("transform", `translate(${f(cx)} ${f(cy)}) scale(${f(zoom)}) translate(${f(-cx)} ${f(-cy)})`);

  // Erlich.
  const eBreath = Math.sin(wall * 1.6) * 1.2;
  const down = slump(t);
  scene.erlich.setAttribute("transform", `translate(${f(erlichLean(t))} ${f(eBreath + down * 6)})`);
  const eFace = ERLICH_FACES[erlichFace(t)] ?? E_LISTEN;
  const eSay = talking("erlich", t);
  const eMouth = eSay < 0 ? eFace.mouth : eSay === 0 ? "closed" : eSay === 2 ? "wide" : "talk";
  scene.erlichMouth.setAttribute("d", ERLICH_MOUTHS[eMouth] ?? "M229 319Q241 323 253 318");
  const eFilled = eMouth !== "closed" && eMouth !== "smile" && eMouth !== "frown";
  scene.erlichMouth.setAttribute("fill", eFilled ? MOUTH : "none");
  scene.erlichHead.setAttribute(
    "transform",
    `rotate(${f(erlichLean(t) * 0.6 - down * 6 + (eSay > 0 ? Math.sin(t * 9) * 0.8 : 0))} 236 340)`,
  );
  scene.erlichBrows.forEach((brow, i) => {
    const pivot = i === 0 ? "214 249" : "258 248";
    const tilt = i === 0 ? eFace.tilt : -eFace.tilt;
    brow.setAttribute("transform", `translate(0 ${f(eFace.brow)}) rotate(${f(tilt)} ${pivot})`);
  });
  scene.erlichIrises.setAttribute("transform", `translate(${f(eFace.gx)} ${f(eFace.gy)})`);
  const eLid = Math.max(eFace.lid, blink(wall, 0.3));
  scene.erlichLids[0]?.setAttribute("d", lidPath(215, 268, 12, 9.5, eLid));
  scene.erlichLids[1]?.setAttribute("d", lidPath(257, 267, 11, 9, eLid));
  scene.erlichSticker.setAttribute("opacity", t >= W.youAreBowlBot + 0.15 ? "1" : "0");

  // Jian-Yang.
  const jBreath = Math.sin(wall * 1.3 + 1) * 1;
  scene.jy.setAttribute("transform", `translate(${f(jyLean(t))} ${f(jBreath)})`);
  const jFace = JY_FACES[jyFace(t)] ?? J_FLAT;
  const jSay = talking("jian_yang", t);
  const jMouth = jSay < 0 ? jFace.mouth : jSay === 0 ? "flat" : jSay === 2 ? "open" : "talk";
  scene.jyMouth.setAttribute("d", JY_MOUTHS[jMouth] ?? "M723 325L743 324");
  scene.jyMouth.setAttribute("fill", jMouth === "talk" || jMouth === "open" ? MOUTH : "none");
  scene.jyHead.setAttribute("transform", `rotate(${f(jyLean(t) * 0.8 + (jyFace(t) === "proud" ? -3 : 0))} 734 352)`);
  scene.jyBrows.forEach((brow, i) => {
    brow.setAttribute("transform", `translate(0 ${f(jFace.brow)}) rotate(${f(i === 0 ? jFace.tilt : -jFace.tilt)} ${i === 0 ? "709 278" : "753 278"})`);
  });
  scene.jyIrises.setAttribute("transform", `translate(${f(jFace.gx)} ${f(jFace.gy)})`);
  const jLid = Math.max(jFace.lid, blink(wall, 1.1));
  scene.jyLids[0]?.setAttribute("d", lidPath(710, 293, 6.2, 3.8, jLid));
  scene.jyLids[1]?.setAttribute("d", lidPath(752, 292, 5.8, 3.6, jLid));

  // Hands and sleeves.
  const rub = scrubbing(t) * Math.sin((t - W.youAreBowlBot) * 2 * Math.PI * 2) * 16;
  const ehx = erlichHand.x(t) + rub + erlichLean(t);
  const ehy = erlichHand.y(t) + eBreath + down * 4;
  const eArm = sleeve([342 + erlichLean(t), 404 + eBreath + down * 6], ehx, ehy, 26);
  scene.erlichArm.setAttribute("d", eArm.d);
  placeHand(scene.erlichHand, ehx, ehy, eArm.angle, erlichGrip(t), false);
  const lifted = phoneUp(t);
  scene.phone.setAttribute("opacity", lifted > 0 ? "1" : "0");
  scene.phone.setAttribute("transform", `translate(${f(ehx + 4)} ${f(ehy - 46)}) rotate(-8) scale(${f(0.7 + 0.3 * lifted)})`);
  const jhx = jyHand.x(t) + jyLean(t);
  const jhy = jyHand.y(t) + jBreath;
  const jArm = sleeve([692 + jyLean(t), 414 + jBreath], jhx, jhy, -22);
  scene.jyArm.setAttribute("d", jArm.d);
  const stripe = sleeve([692 + jyLean(t), 400 + jBreath], jhx, jhy - 10, -26);
  scene.jyStripe.setAttribute("d", stripe.d);
  placeHand(scene.jyHand, jhx, jhy, jArm.angle, jyGrip(t), true);

  // Erlich's fantasy, and the bowl it becomes.
  const cloud = cloudIn(t);
  scene.cloud.setAttribute("opacity", f(cloud));
  scene.cloud.setAttribute("transform", `translate(${f(300 * (1 - cloud))} ${f(270 * (1 - cloud))}) scale(${f(0.4 + 0.6 * cloud)})`);
  const points = linePoints(t);
  scene.line.setAttribute("d", pathOf(points));
  scene.line.setAttribute("stroke-dashoffset", f(1 - lineDraw(t)));
  const m = morph(t);
  const tip = points[points.length - 1] ?? { x: 0, y: 0 };
  const fall = clamp01((t - morphAt) / 0.5);
  scene.bull.setAttribute(
    "transform",
    `translate(${f(tip.x + 2)} ${f(tip.y - 14 + fall * fall * 50)}) rotate(${f(fall * 110)}) scale(${f(lineDraw(t) > 0.98 ? 0.9 + bump(t, W.genius) * 0.3 : 0)})`,
  );
  scene.bull.setAttribute("opacity", f(1 - fall));
  scene.cloudButton.setAttribute("opacity", f(buttonIn(t) * (1 - m)));
  scene.cloudButtonTop.setAttribute("transform", `translate(0 ${f(bump(t, W.pushButtonAgain + 0.15) * 3)})`);
  scene.noHumans.setAttribute("opacity", f(noHumansIn(t) * (1 - m)));
  scene.money.setAttribute("opacity", f(moneyIn(t) * (1 - m)));
  scene.money.setAttribute("transform", `translate(0 ${f(-6 * moneyIn(t))})`);
  scene.rim.setAttribute("opacity", f(clamp01((m - 0.7) / 0.3)));
  scene.bowlFill.setAttribute("d", `${pathOf(BOWL)}Z`);
  scene.bowlFill.setAttribute("opacity", f(clamp01((m - 0.8) / 0.2)));
  const drop = bowlFall(t);
  scene.bowl.setAttribute("opacity", drop > 0 ? "1" : "0");
  scene.bowl.setAttribute("transform", `translate(${f(494 - 172 * drop)} ${f(254 + 244 * drop)}) scale(${f(0.8 + 0.2 * drop)})`);
  scene.steam.setAttribute("opacity", "0");

  // Project Leviathan, then BowlBot.
  const off = clothOff(t);
  scene.cloth.setAttribute("opacity", off >= 1 ? "0" : "1");
  scene.cloth.setAttribute("transform", `translate(${f(off * 190)} ${f(-off * 120)}) rotate(${f(off * 28)}) scale(${f(1 - off * 0.3)})`);
  scene.flapRow.setAttribute("transform", `translate(0 ${f(-52 - 53 * off)}) scale(${f(1 - 0.14 * off)})`);
  flapsAt(t).forEach((flap, i) => {
    const slot = scene.flaps[i];
    if (!slot) return;
    if (scene.shown.flaps[i] !== flap.char) {
      slot.label.textContent = flap.char === " " ? "" : flap.char;
      scene.shown.flaps[i] = flap.char;
    }
    slot.face.setAttribute("transform", `scale(1 ${f(Math.max(0.02, flap.open))})`);
  });
  const wave = armWave(t);
  const armFall = armDrop(t);
  scene.arm.setAttribute(
    "transform",
    `translate(${f(-78 - armFall * 40)} ${f(-70 + armFall * armFall * 66)}) rotate(${f(wave * Math.sin((t - W.smartBowlBot) * 2 * Math.PI * 1.6) * 16 - armFall * 80)})`,
  );
  scene.arm.setAttribute("opacity", off > 0.4 ? f(1 - clamp01((t - W.not - 0.9) / 0.4)) : "0");
  scene.light.setAttribute("fill", lightOut(t) > 0.5 ? "#4a443c" : t > W.smartBowlBot && Math.sin(wall * 6) > 0 ? "#f3c1aa" : TERRACOTTA);
  const tag = tagOn(t);
  scene.tag.setAttribute("opacity", tag > 0 ? "1" : "0");
  scene.tag.setAttribute("transform", `translate(${f(40 + 60 * (1 - tag))} -30) rotate(${f(bump(t, W.fourteen) * 10)}) scale(${f(1 + bump(t, W.fourteen) * 0.25)})`);
  scene.tagCheap.setAttribute("opacity", t < W.fourteen ? "1" : "0");
  scene.tagPrice.setAttribute("opacity", t >= W.fourteen ? "1" : "0");

  // The palapa, the term sheet and the sponge.
  const palapaBump = Math.max(bump(t, W.halfAMillion), bump(t, W.palapa));
  scene.palapa.setAttribute(
    "transform",
    `translate(${f(palapaX(t))} ${f(456 - palapaBump * 6)}) scale(${f(0.86 + palapaBump * 0.1)})`,
  );
  const terms = termsIn(t);
  scene.terms.setAttribute("opacity", terms > 0 ? "1" : "0");
  scene.terms.setAttribute("transform", `translate(${f(486 + 92 * terms)} ${f(508 + 4 * (1 - terms))})`);
  const chip = chipIn(t);
  scene.chip.setAttribute("opacity", chip > 0 ? "1" : "0");
  scene.chip.setAttribute("transform", `translate(-50 6) scale(${f((1.6 - 0.6 * chip) * (1 + bump(t, W.tenPercentAgain) * 0.2))})`);
  scene.stamp.setAttribute("opacity", t >= stampAt ? "1" : "0");
  scene.stamp.setAttribute("transform", `translate(30 8) rotate(-6) scale(${f(1 + (1 - clamp01((t - stampAt) / 0.14)) * 0.6)})`);
  const sx = t >= W.youAreBowlBot + 0.7 ? ehx + 6 : spongeX(t);
  const sy = t >= W.youAreBowlBot + 0.7 ? ehy + 4 : 512;
  scene.sponge.setAttribute("opacity", t >= W.cleanTheSink - 0.1 ? "1" : "0");
  scene.sponge.setAttribute("transform", `translate(${f(sx)} ${f(sy)}) rotate(${f(-6 + rub * 0.5)})`);
  scene.fish.setAttribute("opacity", t >= W.evenThough + 0.25 ? "1" : "0");
  scene.fish.setAttribute("transform", `translate(${f(fishX(t))} 516) scale(1.3)`);
  const note = notify(t);
  scene.notify.setAttribute("opacity", f(clamp01(note * 1.4)));
  scene.notify.setAttribute("transform", `translate(-30 ${f(-150 + 16 * (1 - note))}) scale(${f(0.86 + 0.14 * note)})`);
  const fly = sticker(t);
  scene.flyingSticker.setAttribute("opacity", fly > 0 && fly < 1 ? "1" : "0");
  scene.flyingSticker.setAttribute(
    "transform",
    `translate(${f(560 + (206 + erlichLean(t) - 560) * fly)} ${f(420 + (418 - 420) * fly - Math.sin(Math.PI * fly) * 90)}) rotate(${f(-8 - 200 * (1 - fly))})`,
  );

  // Speech bubbles.
  let index = -1;
  PAGES.forEach((page, i) => {
    if (t >= page[1]) index = i;
  });
  const page = PAGES[index];
  if (index !== scene.shown.page) {
    for (const who of /** @type {const} */ (["erlich", "jian_yang"])) {
      const b = scene.bubbles[who];
      if (page && page[0] === who) {
        b.text.textContent = page[3];
        b.box.dataset.show = "true";
      } else {
        b.box.dataset.show = "false";
      }
    }
    scene.shown.page = index;
  }
  if (page) {
    const b = scene.bubbles[page[0]];
    const grow = clamp01((t - page[1]) / 0.16);
    b.box.style.opacity = f(grow);
    b.box.style.transform = `translateY(${f((1 - grow) * 6)}px) scale(${f(0.96 + 0.04 * grow)})`;
  }
}
